import { useState, useMemo, useCallback, useEffect, useRef } from "react";
import Table from "examples/Tables/Table";
import VuiBox from "components/VuiBox";
import VuiTypography from "components/VuiTypography";
import VuiBadge from "components/VuiBadge";
import VuiButton from "components/VuiButton";
import { FaBan, FaPen, FaRedo, FaTrash } from "react-icons/fa";
import { HiOutlineBuildingOffice, HiOutlineBuildingOffice2 } from "react-icons/hi2";
import EditClientDialog from "./EditClientDialog";
import DeleteClientDialog from "./DeleteClientDialog";
import useCommon from "hooks/useCommon";
import { updateClient } from "store/slice/clientSlice";
import { getClients } from "store/slice/clientSlice";
import { updateAdminStatus } from "store/slice/clientSlice";
import StatusConfirmationDialog from "./StatusConfirmationDialog";
import { deleteClient } from "store/slice/clientSlice";
import { reserDeleteAdminState } from "store/slice/clientSlice";
import NoResults from "components/NoResult";
import Tooltip from "@mui/material/Tooltip";
import { Dialog, DialogContent, DialogTitle } from "@mui/material";
import VuiTooltip from "components/VuiTooltip";

/* ---------------- Cells ---------------- */

// const generateAvatarText = (name = "") => {
//   const parts = name.split(" ");
//   return (parts[0]?.[0] + (parts[1]?.[0] || "")).toUpperCase();
// };

function HotelChip({ name }) {
  const textRef = useRef(null);
  const [showTooltip, setShowTooltip] = useState(false);

  useEffect(() => {
    const el = textRef.current;
    if (el) {
      setShowTooltip(el.scrollWidth > el.clientWidth);
    }
  }, [name]);

  const chip = (
    <VuiBox
      px={1.5}
      py={0.25}
      borderRadius="md"
      display="inline-flex"
      alignItems="center"
      gap={0.5}
      sx={({ palette: { info, primary, white } }) => ({
        background: primary.main,
        color: white.main,
        fontSize: "12px",
        maxWidth: "180px",
        whiteSpace: "nowrap",
        overflow: "hidden",
        textOverflow: "ellipsis",
        cursor: showTooltip ? "pointer" : "default",
      })}
    >
      <HiOutlineBuildingOffice fontSize="small" style={{ flexShrink: 0 }} />

      <span
        ref={textRef}
        style={{
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
      >
        {name}
      </span>
    </VuiBox>
  );

  return showTooltip ? <VuiTooltip title={name}>{chip}</VuiTooltip> : chip;
}

function ClientCell({ name, email }) {
  return (
    <VuiBox display="flex" alignItems="center" px={1} py={0.5} sx={{ width: "100%" }}>
      <VuiBox display="flex" flexDirection="column" sx={{ minWidth: 0 }}>
        <VuiTypography
          variant="button"
          fontWeight="medium"
          sx={{
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
            maxWidth: "100%",
          }}
        >
          {name}
        </VuiTypography>

        <VuiTypography
          variant="caption"
          sx={{
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
            maxWidth: "100%",
          }}
        >
          {email}
        </VuiTypography>
      </VuiBox>
    </VuiBox>
  );
}

function StatusBadge({ status, emailStatus }) {
  const isActive = status === 1;
  const isEmailVerified = emailStatus;

  return (
    <VuiBadge
      variant="standard"
      badgeContent={!isEmailVerified ? "Inactive" : isActive ? "Active" : "Deactivated"}
      color={isActive ? "success" : "error"}
      size="xs"
      container
      sx={({ palette: { white, success, error }, borders: { borderRadius, borderWidth } }) => ({
        background: isActive ? success.main : "unset",
        border: `${borderWidth[1]} solid ${isActive ? success.main : error.main}`,
        borderRadius: borderRadius.md,
        color: isActive ? white.main : error.main,
      })}
    />
  );
}

function HotelsCell({ hotels = [] }) {
  const [open, setOpen] = useState(false);

  if (!hotels.length) {
    return <VuiTypography variant="caption">-</VuiTypography>;
  }

  const visibleHotels = hotels.slice(0, 1);
  const remainingCount = hotels.length - 1;

  return (
    <>
      <VuiBox display="flex" gap={1}>
        {visibleHotels.map((name, index) => (
          <HotelChip key={index} name={name} />
        ))}

        {remainingCount > 0 && (
          <VuiBox
            px={1.5}
            py={0.25}
            borderRadius="md"
            display="inline-flex"
            alignItems="center"
            gap={0.5}
            onClick={() => setOpen(true)}
            sx={({ palette: { info, white, primary } }) => ({
              cursor: "pointer",
              background: primary.main,
              color: white.main,
              fontSize: "12px",
            })}
          >
            {`+${remainingCount}`}
          </VuiBox>
        )}
      </VuiBox>

      {/* -------- Dialog -------- */}
      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>
          <VuiTypography variant="button" fontWeight="regular">
            Associated Hotels
          </VuiTypography>
        </DialogTitle>

        <DialogContent>
          <VuiBox display="flex" flexDirection="column" gap={1}>
            {hotels.map((name, index) => (
              <VuiBox
                key={index}
                px={1.5}
                py={0.75}
                mb={1.5}
                borderRadius="md"
                display="flex"
                alignItems="center"
                gap={2}
                sx={({ palette: { white, primary } }) => ({
                  background: "rgba(255, 255, 255, 0.08)",
                  border: "1px solid rgba(255, 255, 255, 0.25)",
                  color: primary.main,
                  backdropFilter: "blur(6px)",
                })}
              >
                <HiOutlineBuildingOffice2 />

                <VuiTypography variant="caption" sx={{ wordBreak: "break-word" }}>
                  {name}
                </VuiTypography>
              </VuiBox>
            ))}
          </VuiBox>
        </DialogContent>
      </Dialog>
    </>
  );
}

function ActionButtons({ user, onEdit, onToggleStatus, onDelete }) {
  const isActive = user.status === 1;
  const emailVerified = user?.emailVerified;

  return (
    <VuiBox display="flex" justifyContent="center" gap={1}>
      {/* EDIT */}
      <VuiTooltip title={!emailVerified ? "Cannot edit" : "Edit client"}>
        <VuiButton
          size="small"
          variant="outlined"
          color="primary"
          disabled={!emailVerified || !isActive}
          onClick={onEdit}
          sx={{ px: 1 }}
        >
          <FaPen size={12} />
        </VuiButton>
      </VuiTooltip>

      {/* ACTIVATE / DEACTIVATE / UNVERIFIED */}
      <VuiTooltip
        title={!emailVerified ? "User email is not verified" : isActive ? "Deactivate" : "Activate"}
      >
        <VuiButton
          size="small"
          variant="outlined"
          color={!emailVerified ? "error" : isActive ? "primary" : "success"}
          onClick={onToggleStatus}
          sx={{ px: 1 }}
        >
          {!emailVerified ? (
            <FaBan size={12} />
          ) : isActive ? (
            <FaBan size={12} />
          ) : (
            <FaRedo size={12} />
          )}
        </VuiButton>
      </VuiTooltip>

      {/* DELETE */}
      <VuiTooltip title="Delete">
        <VuiButton size="small" variant="outlined" color="error" onClick={onDelete} sx={{ px: 1 }}>
          <FaTrash size={12} />
        </VuiButton>
      </VuiTooltip>
    </VuiBox>
  );
}

/* ---------------- Main Component ---------------- */

const ClientsTable = ({ handleNotify, search }) => {
  const { FORMAT_DATE, DISPATCH, STORE } = useCommon();
  const { list: clients, pagination, listLoading, listError } = STORE((state) => state.clients);
  const { deleteSuccess } = STORE((state) => state.hotels);

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);

  const [editOpen, setEditOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmAction, setConfirmAction] = useState(null); // "activate" | "deactivate"
  const [targetUser, setTargetUser] = useState(null);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deletingId, setDeletingId] = useState(null);

  const handleEditOpen = (id) => {
    setEditingId(id);
    setEditOpen(true);
  };

  const handleEditClose = () => {
    setEditOpen(false);
    setEditingId(null);
  };
  const handleDeleteClick = (id) => {
    setDeletingId(id);
    setDeleteOpen(true);
  };

  const handleCloseDelete = () => {
    setDeletingId(null);
    setDeleteOpen(false);
    DISPATCH(reserDeleteAdminState());
  };

  const handleUpdateClient = async (payload) => {
    await DISPATCH(updateClient(payload))
      .unwrap()
      .then((res) => {
        const success = res?.success;

        if (success) {
          handleEditClose();
          handleNotify({
            color: success ? "success" : "error",
            title: success ? "Client Added" : "Error",
            content: res?.message || "Client created successfully",
            icon: success ? "check" : "warning",
            show: true,
          });
        }
      })
      .catch((err) => {
        handleEditClose();
        handleNotify({
          color: "error",
          title: "Error",
          content: err?.message || "Failed to create client",
          icon: "warning",
          show: true,
        });
      });
    setEditingId(null);
    setEditOpen(false);
  };

  const handleStatusUpdate = async (userId, action) => {
    await DISPATCH(updateAdminStatus({ userId, action }))
      .unwrap()
      .then((res) => {
        const success = res?.success;

        if (success) {
          setConfirmOpen(false);
          setTargetUser(null);
          setConfirmAction(null);
          handleNotify({
            color: success ? "success" : "error",
            title: success ? "Admin Updated" : "Error",
            content: "Admin status updated successfully",
            icon: success ? "check" : "warning",
            show: true,
          });
        }
      })
      .catch((err) => {
        setConfirmOpen(false);
        setTargetUser(null);
        setConfirmAction(null);
        handleNotify({
          color: "error",
          title: "Failed to update admin",
          content: err?.message || "Failed to update admin",
          icon: "warning",
          show: true,
        });
      });
  };

  const handledeleteConfirmation = async () => {
    const userId = deletingId || null;
    await DISPATCH(deleteClient(userId))
      .unwrap()
      .then((res) => {
        const success = res?.apiRes?.success;

        if (success) {
          handleNotify({
            color: success ? "success" : "error",
            title: success ? "Admin Deleted" : "Error",
            content: res?.message || "Admin deleted successfully",
            icon: success ? "check" : "warning",
            show: true,
          });
          handleCloseDelete();
        }
      })
      .catch((err) => {
        handleNotify({
          color: "error",
          title: "Failed to delete user",
          content: err?.message || "Failed to delete admin",
          icon: "warning",
          show: true,
        });
        handleCloseDelete();
      });
  };

  const filteredClients = useMemo(() => {
    if (!search || search.trim().length < 3) return clients;

    const q = search.toLowerCase();
    return clients.filter(
      (c) =>
        c.name?.toLowerCase().includes(q) ||
        c.email?.toLowerCase().includes(q) ||
        c.hotelId?.some((h) => h.name?.toLowerCase().includes(q))
    );
  }, [clients, search]);

  useEffect(() => {
    // if (clients.length > 0) return;
    DISPATCH(getClients());
  }, [DISPATCH, deleteSuccess]);

  const tableData = useMemo(() => {
    return {
      columns: [
        { name: "client", align: "left" },
        { name: "hotels", align: "left" },
        { name: "dateAdded", align: "center" },
        { name: "status", align: "center" },
        { name: "action", align: "center" },
      ],

      rows: filteredClients.map((c) => ({
        client: <ClientCell name={c.name} email={c.email} />,

        hotels: <HotelsCell hotels={c.hotelId?.map((h) => h.name)} />,

        dateAdded: (
          <VuiTypography variant="caption" fontWeight="medium">
            {FORMAT_DATE(c.createdAt)}
          </VuiTypography>
        ),

        status: <StatusBadge status={c.status} emailStatus={c.emailVerified} />,

        action: (
          <ActionButtons
            user={c}
            onEdit={() => handleEditOpen(c._id)}
            onDelete={() => handleDeleteClick(c._id)}
            onToggleStatus={() => {
              if (!c?.emailVerified) return;
              setTargetUser(c);
              setConfirmAction(c?.status === 1 ? "deactivate" : "activate");
              setConfirmOpen(true);
            }}
          />
        ),
      })),
    };
  }, [clients, handleEditOpen, handleDeleteClick]);

  return (
    <>
      {!listLoading && filteredClients.length === 0 ? (
        <NoResults title="No Client found" search={search} />
      ) : (
        <Table
          columns={tableData.columns}
          rows={tableData.rows}
          loading={listLoading}
          pagination
          paginationMode="client"
          page={page}
          rowsPerPage={rowsPerPage}
          total={tableData.rows.length}
          onPageChange={(newPage) => setPage(newPage)}
          onRowsPerPageChange={(newLimit) => {
            setRowsPerPage(newLimit);
            setPage(0);
          }}
        />
      )}

      <EditClientDialog
        open={editOpen}
        clientId={editingId}
        onSave={(payload) => {
          handleUpdateClient(payload);
        }}
        onClose={() => {
          setEditOpen(false);
          setEditingId(null);
        }}
      />

      <StatusConfirmationDialog
        open={confirmOpen}
        action={confirmAction}
        userName={targetUser?.name}
        onClose={() => {
          setConfirmOpen(false);
          setTargetUser(null);
          setConfirmAction(null);
        }}
        onConfirm={() => {
          handleStatusUpdate(targetUser._id, confirmAction);
        }}
      />

      <DeleteClientDialog
        open={deleteOpen}
        clientId={deletingId}
        onConfirm={handledeleteConfirmation}
        onClose={() => {
          setDeleteOpen(false);
          setDeletingId(null);
        }}
      />
    </>
  );
};

export default ClientsTable;
